/**
 * Auto-save functionality for profile and preferences forms
 */
import { showToast } from '../../main';
import { TOAST_EMAIL_UPDATED } from '../../utils/app-strings';
import { profileApi, preferencesApi } from '../../api';
import { store } from '../../state';
import { debounce, showSaveIndicator, setupDeselectableRadios } from '../../utils/form';
import { formatHeight, getUnits, sliderToKm, sliderToDisplayLabel, DISTANCE_SLIDER } from '../../utils';
import { showEmailVerifyModal } from './email-verify-modal';
import { MAX_HEADLINE_LENGTH, MAX_BIO_LENGTH, MIN_AGE, MAX_AGE } from '../../config';
import { countUnansweredFromDOM, updateSectionProgress, updateFieldAnsweredStates } from '../../options';

/**
 * Map field IDs to their indicator IDs (for consolidated indicators)
 */
const INDICATOR_MAP: Record<string, string> = {
  'height_cm': 'height',
  'weight_lb': 'weight',
  'weight_kg': 'weight',
};

/**
 * Get the indicator ID for a field (uses mapping or defaults to field ID)
 */
function getIndicatorId(fieldId: string): string {
  return INDICATOR_MAP[fieldId] || fieldId;
}

/**
 * Helper to update character counter display
 */
function updateCharCounter(inputId: string, counterId: string, maxLength: number): void {
  const input = document.getElementById(inputId) as HTMLInputElement | HTMLTextAreaElement | null;
  const counter = document.getElementById(counterId);
  if (!input || !counter) return;

  const current = input.value.length;
  const currentFormatted = current.toLocaleString();
  const maxFormatted = maxLength.toLocaleString();
  counter.textContent = `${currentFormatted}/${maxFormatted}`;
}

/**
 * Helper to get input value (returns null for empty strings)
 */
function getInputValue(id: string): string | null {
  const el = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
  if (!el) return null;
  const val = el.value.trim();
  return val || null;
}

/**
 * Helper to get select value (returns null for empty strings)
 */
function getSelectValue(id: string): string | null {
  const el = document.getElementById(id) as HTMLSelectElement | null;
  if (!el) return null;
  return el.value || null;
}

/**
 * Helper to get radio group value (returns null if none selected)
 */
function getRadioGroupValue(groupId: string): string | null {
  const group = document.getElementById(groupId);
  if (!group) return null;
  const checked = group.querySelector('input[type="radio"]:checked') as HTMLInputElement | null;
  return checked?.value || null;
}

/**
 * Helper to get radio group with "other" pattern value
 * If "other" radio is selected, returns the value from the _other text field
 */
function getRadioWithOtherValue(groupId: string): string | null {
  const group = document.getElementById(groupId);
  if (!group) return null;
  const checked = group.querySelector('input[type="radio"]:checked') as HTMLInputElement | null;
  if (!checked) return null;

  if (checked.value === 'other') {
    const otherInput = document.getElementById(`${groupId}_other`) as HTMLInputElement | null;
    return otherInput?.value.trim() || null;
  }

  return checked.value;
}

/**
 * Helper to get number input value
 */
function getNumberValue(id: string): number | null {
  const el = document.getElementById(id) as HTMLInputElement | null;
  if (!el || !el.value) return null;
  const num = parseInt(el.value);
  return isNaN(num) ? null : num;
}

/**
 * Helper to get array from comma-separated input
 */
function getArrayValue(id: string): string[] | null {
  const val = getInputValue(id);
  if (!val) return null;
  return val.split(',').map(s => s.trim()).filter(s => s);
}

/**
 * Helper to get value for a field with PNTA checkbox support
 * If PNTA checkbox is checked, returns '[pnta]', otherwise returns the original value
 */
function getPntaOrValue(fieldId: string, originalValue: string | null): string | null {
  // Check if there's a PNTA checkbox for this field (could be part of a comma-separated list)
  const pntaCheckbox = document.querySelector(
    `input[data-pnta-for="${fieldId}"], input[data-pnta-for*="${fieldId},"], input[data-pnta-for*=",${fieldId}"]`
  ) as HTMLInputElement | null;

  if (pntaCheckbox?.checked) {
    return '[pnta]';
  }
  return originalValue;
}

/**
 * Helper to get array value for a field with PNTA checkbox support
 * If PNTA checkbox is checked, returns ['pnta'], otherwise returns the original array
 */
function getPntaOrArrayValue(fieldId: string, originalValue: string[] | null): string[] | null {
  const pntaCheckbox = document.querySelector(`input[data-pnta-for="${fieldId}"]`) as HTMLInputElement | null;

  if (pntaCheckbox?.checked) {
    return ['pnta'];
  }
  return originalValue;
}

/**
 * Helper to get array from checkbox group
 */
function getCheckboxGroupValue(name: string): string[] {
  const checkboxes = document.querySelectorAll(`input[name="${name}"]:checked`);
  return Array.from(checkboxes).map(cb => (cb as HTMLInputElement).value);
}

/**
 * Helper to get radio group value with PNTA support
 * If PNTA checkbox is checked, returns 'pnta', otherwise returns the radio value
 */
function getRadioValueWithPnta(groupId: string): string | null {
  const pntaCb = document.querySelector(`input[data-pnta-for-group="${groupId}"]`) as HTMLInputElement;
  if (pntaCb?.checked) return 'pnta';
  return getRadioGroupValue(groupId);
}

/**
 * Helper to get radio group with "other" pattern value, with PNTA support
 * If PNTA checkbox is checked, returns 'pnta'
 */
function getRadioWithOtherValueWithPnta(groupId: string): string | null {
  const pntaCb = document.querySelector(`input[data-pnta-for-group="${groupId}"]`) as HTMLInputElement;
  if (pntaCb?.checked) return 'pnta';
  return getRadioWithOtherValue(groupId);
}

/**
 * Helper to get checkbox group values with PNTA support
 * If PNTA checkbox is checked, returns ['pnta'], otherwise returns checked values
 */
function getCheckboxValuesWithPnta(name: string): string[] {
  const pntaCb = document.querySelector(`input[data-pnta-for-group="${name}"]`) as HTMLInputElement;
  if (pntaCb?.checked) return ['pnta'];
  return getCheckboxGroupValue(name);
}

/**
 * Helper to get multi-select with "other" pattern value, with PNTA support
 * If PNTA checkbox is checked, returns ['pnta']
 */
function getMultiSelectWithOtherValueWithPnta(groupId: string): string[] | null {
  const pntaCb = document.querySelector(`input[data-pnta-for-group="${groupId}"]`) as HTMLInputElement;
  if (pntaCb?.checked) return ['pnta'];
  return getMultiSelectWithOtherValue(groupId);
}

/**
 * Helper to get value from dropdown + other pattern
 * If dropdown is "other", returns the value from the _other text field
 * Otherwise returns the dropdown value
 */
function getDropdownWithOtherValue(selectId: string): string | null {
  const selectEl = document.getElementById(selectId) as HTMLSelectElement | null;
  if (!selectEl) return null;

  const selectedValue = selectEl.value;
  if (!selectedValue) return null;

  if (selectedValue === 'other') {
    const otherInput = document.getElementById(`${selectId}_other`) as HTMLInputElement | null;
    return otherInput?.value.trim() || null;
  }

  return selectedValue;
}

/**
 * Helper to get array from multi-select + other pattern
 * Combines checked values with custom values from _other text field
 * Filters out "other" from the result
 */
function getMultiSelectWithOtherValue(groupId: string): string[] | null {
  const checkboxes = document.querySelectorAll(`input[name="${groupId}"]:checked`);
  const values: string[] = [];

  checkboxes.forEach(cb => {
    const val = (cb as HTMLInputElement).value;
    if (val && val !== 'other') {
      values.push(val);
    }
  });

  // Check if "other" checkbox is checked and get custom values
  const otherCheckbox = document.querySelector(`input[name="${groupId}"][value="other"]`) as HTMLInputElement | null;
  if (otherCheckbox?.checked) {
    const otherInput = document.getElementById(`${groupId}_other`) as HTMLInputElement | null;
    if (otherInput?.value.trim()) {
      const otherValues = otherInput.value.split(',').map(s => s.trim()).filter(s => s);
      values.push(...otherValues);
    }
  }

  return values.length > 0 ? values : null;
}

/**
 * Helper to set a nested property in an object
 */
function setNestedValue(obj: Record<string, any>, path: string, value: any): void {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) {
      current[parts[i]] = {};
    }
    current = current[parts[i]];
  }
  current[parts[parts.length - 1]] = value;
}

// Track which fields were rejected by moderation (set after each save)
let lastRejectedFields: string[] = [];

// Field name mapping: indicator IDs to moderation field names returned by server
const MODERATION_FIELD_MAP: Record<string, string[]> = {
  'user_display_str': ['first_name'],
  'bio': ['bio'],
  'school': ['school'],
  'company': ['company'],
  'job_title': ['job_title'],
  'headline': ['about.headline'],
  'interests': ['about.interests'],
  'hometown': ['identity.hometown'],
  'pronouns': ['identity.pronouns_custom'],
};

/**
 * Show save indicator, checking for moderation rejections.
 * If the field was rejected by moderation, shows "Rejected" instead of "Saved".
 */
function showSaveIndicatorWithModeration(indicatorId: string, success: boolean, fieldId?: string): void {
  if (!success) {
    showSaveIndicator(indicatorId, false);
    return;
  }
  // Check if this field was rejected by moderation
  const modFields = fieldId ? (MODERATION_FIELD_MAP[fieldId] || [fieldId]) : [];
  const wasRejected = modFields.some(f => lastRejectedFields.includes(f));
  if (wasRejected) {
    showSaveIndicator(indicatorId, false, 'Rejected');
  } else {
    showSaveIndicator(indicatorId, true);
  }
}

export function getLastRejectedFields(): string[] {
  return lastRejectedFields;
}

/**
 * Collect and save profile data from form
 */
export async function saveProfile(): Promise<boolean> {
  const firstNamePnta = document.getElementById('user_display_str_pnta') as HTMLInputElement;
  let firstName: string;
  if (firstNamePnta?.checked) {
    firstName = '[pnta]';
  } else {
    firstName = (document.getElementById('user_display_str') as HTMLInputElement).value.trim();
  }
  const birthDate = (document.getElementById('birth_date') as HTMLInputElement).value;

  // Require first_name and birth_date for initial save
  // '[pnta]' is truthy so it passes this check
  if (!firstName || !birthDate) {
    console.warn('saveProfile skipped: missing', !firstName ? 'first_name' : 'birth_date');
    return false;
  }

  const data: any = {
    first_name: firstName,
    birth_date: birthDate,
  };

  const genderPnta = document.querySelector('input[data-pnta-for-group="gender_id"]') as HTMLInputElement;
  if (genderPnta?.checked) {
    data.gender_id = null;
  } else {
    const genderId = getRadioGroupValue('gender_id');
    data.gender_id = genderId ? parseInt(genderId) : null;
  }

  const relStatusPnta = document.querySelector('input[data-pnta-for-group="relationship_status_id"]') as HTMLInputElement;
  if (relStatusPnta?.checked) {
    data.relationship_status_id = null;
  } else {
    const relationshipStatusId = getRadioGroupValue('relationship_status_id');
    data.relationship_status_id = relationshipStatusId ? parseInt(relationshipStatusId) : null;
  }

  // Text fields with PNTA support
  data.bio = getPntaOrValue('bio', getInputValue('bio'));
  data.city = getPntaOrValue('city', getInputValue('city'));
  data.state = getPntaOrValue('city', getInputValue('state')); // location shares one PNTA checkbox
  data.country = getPntaOrValue('city', getInputValue('country')); // location shares one PNTA checkbox
  data.school = getPntaOrValue('school', getInputValue('school'));
  data.company = getPntaOrValue('company', getInputValue('company'));
  data.job_title = getPntaOrValue('job_title', getInputValue('job_title'));
  data.height_cm = getNumberValue('height_cm');

  // Collect profile_json fields
  const profileJson: Record<string, any> = {};

  // Identity section (using PNTA-aware getters)
  const pronouns = getCheckboxValuesWithPnta('pronouns');
  setNestedValue(profileJson, 'identity.pronouns', pronouns);

  const pronounsCustom = getInputValue('pronouns_custom');
  if (pronounsCustom !== null) setNestedValue(profileJson, 'identity.pronouns_custom', pronounsCustom);

  const sexualOrientation = getMultiSelectWithOtherValueWithPnta('sexual_orientation');
  if (sexualOrientation !== null) setNestedValue(profileJson, 'identity.sexual_orientation', sexualOrientation);

  const ethnicity = getCheckboxValuesWithPnta('ethnicity');
  setNestedValue(profileJson, 'identity.ethnicity', ethnicity);

  const religion = getRadioWithOtherValueWithPnta('religion');
  if (religion !== null) setNestedValue(profileJson, 'identity.religion', religion);

  const politicalView = getRadioValueWithPnta('political_view');
  if (politicalView !== null) setNestedValue(profileJson, 'identity.political_view', politicalView);

  const languages = getMultiSelectWithOtherValueWithPnta('languages');
  if (languages !== null) setNestedValue(profileJson, 'identity.languages', languages);

  const zodiacSign = getRadioValueWithPnta('zodiac_sign');
  if (zodiacSign !== null) setNestedValue(profileJson, 'identity.zodiac_sign', zodiacSign);

  const hometown = getPntaOrValue('hometown', getInputValue('hometown'));
  if (hometown !== null) setNestedValue(profileJson, 'identity.hometown', hometown);

  // Body section - weight (handle both lb and kg inputs, store as lb)
  const weightLb = getNumberValue('weight_lb');
  const weightKg = getNumberValue('weight_kg');
  if (weightLb !== null) {
    setNestedValue(profileJson, 'body.weight_lb', weightLb);
  } else if (weightKg !== null) {
    // Convert kg to lb for storage
    setNestedValue(profileJson, 'body.weight_lb', Math.round(weightKg / 0.453592));
  } else {
    // Both empty (e.g. PNTA checked) - clear stored value
    setNestedValue(profileJson, 'body.weight_lb', null);
  }

  const bodyType = getRadioValueWithPnta('body_type');
  if (bodyType !== null) setNestedValue(profileJson, 'body.body_type', bodyType);

  // Lifestyle section (using PNTA-aware getters)
  const smoking = getRadioValueWithPnta('smoking');
  if (smoking !== null) setNestedValue(profileJson, 'lifestyle.smoking', smoking);

  const drinking = getRadioValueWithPnta('drinking');
  if (drinking !== null) setNestedValue(profileJson, 'lifestyle.drinking', drinking);

  const cannabis = getRadioValueWithPnta('cannabis');
  if (cannabis !== null) setNestedValue(profileJson, 'lifestyle.cannabis', cannabis);

  const drugs = getRadioValueWithPnta('drugs');
  if (drugs !== null) setNestedValue(profileJson, 'lifestyle.drugs', drugs);

  const exercise = getRadioValueWithPnta('exercise');
  if (exercise !== null) setNestedValue(profileJson, 'lifestyle.exercise', exercise);

  // Kids section (using PNTA-aware getters)
  const hasKids = getRadioValueWithPnta('has_kids');
  if (hasKids !== null) setNestedValue(profileJson, 'kids.has_kids', hasKids);

  const custody = getRadioValueWithPnta('custody');
  if (custody !== null) setNestedValue(profileJson, 'kids.custody', custody);

  const wantsMoreKids = getRadioValueWithPnta('wants_more_kids');
  if (wantsMoreKids !== null) setNestedValue(profileJson, 'kids.wants_more_kids', wantsMoreKids);

  // Pets section (using PNTA-aware getters) - always send as array
  const petsHas = getCheckboxValuesWithPnta('pets_has');
  setNestedValue(profileJson, 'pets.has', petsHas);

  const petsWants = getCheckboxValuesWithPnta('pets_wants');
  setNestedValue(profileJson, 'pets.wants', petsWants);

  // Background section (using PNTA-aware getters)
  const educationLevel = getRadioValueWithPnta('education_level');
  if (educationLevel !== null) setNestedValue(profileJson, 'background.education_level', educationLevel);

  const employmentStatus = getRadioValueWithPnta('employment_status');
  if (employmentStatus !== null) setNestedValue(profileJson, 'background.employment_status', employmentStatus);

  const major = getRadioWithOtherValueWithPnta('major');
  if (major !== null) setNestedValue(profileJson, 'background.major', major);

  const careerField = getRadioWithOtherValueWithPnta('career_field');
  if (careerField !== null) setNestedValue(profileJson, 'background.career_field', careerField);

  const incomeRange = getRadioValueWithPnta('income_range');
  if (incomeRange !== null) setNestedValue(profileJson, 'background.income_range', incomeRange);

  // About section (with PNTA support)
  const headline = getPntaOrValue('headline', getInputValue('headline'));
  if (headline !== null) setNestedValue(profileJson, 'about.headline', headline);

  const interests = getPntaOrArrayValue('interests', getArrayValue('interests'));
  if (interests !== null) setNestedValue(profileJson, 'about.interests', interests);

  // Relationship section (using PNTA-aware getters)
  const openToRelocation = getRadioValueWithPnta('open_to_relocation');
  if (openToRelocation !== null) setNestedValue(profileJson, 'relationship.open_to_relocation', openToRelocation);

  const openToLongDistance = getRadioValueWithPnta('open_to_long_distance');
  if (openToLongDistance !== null) setNestedValue(profileJson, 'relationship.open_to_long_distance', openToLongDistance);

  const relationshipStructure = getRadioValueWithPnta('relationship_structure');
  if (relationshipStructure !== null) setNestedValue(profileJson, 'relationship.structure', relationshipStructure);

  const relationshipIntent = getCheckboxValuesWithPnta('relationship_intent');
  setNestedValue(profileJson, 'relationship.intent', relationshipIntent);

  // Preferences section - compound intent entries
  const intentEntries = collectIntentEntriesFromDOM('profile-intent-entries-list');
  setNestedValue(profileJson, 'preferences.intent_entries', intentEntries);

  // Disclosures section
  const disclosuresPnta = document.querySelector('input[data-pnta-for-group="disclosures"]') as HTMLInputElement;
  if (disclosuresPnta?.checked) {
    setNestedValue(profileJson, 'disclosures.inclusive_of_trans_nb', false);
    setNestedValue(profileJson, 'disclosures.open_to_enm_poly', false);
    setNestedValue(profileJson, 'disclosures.no_other_groups', false);
    setNestedValue(profileJson, 'disclosures.pnta', true);
  } else {
    const inclusiveOfTransNb = document.getElementById('inclusive_of_trans_nb') as HTMLInputElement;
    if (inclusiveOfTransNb) setNestedValue(profileJson, 'disclosures.inclusive_of_trans_nb', inclusiveOfTransNb.checked);

    const openToEnmPoly = document.getElementById('open_to_enm_poly') as HTMLInputElement;
    if (openToEnmPoly) setNestedValue(profileJson, 'disclosures.open_to_enm_poly', openToEnmPoly.checked);

    const noOtherGroups = document.getElementById('no_other_groups') as HTMLInputElement;
    if (noOtherGroups) setNestedValue(profileJson, 'disclosures.no_other_groups', noOtherGroups.checked);

    setNestedValue(profileJson, 'disclosures.pnta', false);
  }

  // Sexual section (multiselect) - using PNTA-aware getters
  const sexualRole = getCheckboxValuesWithPnta('sexual_role');
  setNestedValue(profileJson, 'sexual.role', sexualRole);

  const sexualDynamics = getCheckboxValuesWithPnta('sexual_dynamics');
  setNestedValue(profileJson, 'sexual.dynamics', sexualDynamics);

  // Add profile_json to data if not empty
  if (Object.keys(profileJson).length > 0) {
    data.profile_json = profileJson;
  }

  try {
    const res = await profileApi.update(data);
    lastRejectedFields = res.data.rejected_fields || [];
    const state = store.getState();
    if (state.user) {
      store.setUser({
        ...state.user,
        email: res.data.email || state.user.email,
        phone_number: res.data.phone_number ?? state.user.phone_number,
        email_verified: res.data.email_verified ?? state.user.email_verified,
        profile: res.data,
      });
    }
    return true;
  } catch (err) {
    console.error('Failed to save profile:', err);
    lastRejectedFields = [];
    return false;
  }
}

/**
 * Collect and save preferences data from form
 */
export async function savePreferences(): Promise<boolean> {
  const ageMinInput = document.getElementById('age_min') as HTMLInputElement;
  const ageMaxInput = document.getElementById('age_max') as HTMLInputElement;
  const distanceInput = document.getElementById('distance_max_km') as HTMLInputElement;
  const distanceEnabledCheckbox = document.getElementById('distance-enabled') as HTMLInputElement;

  const data: any = {
    live_typing_enabled: (document.getElementById('live_typing_enabled') as HTMLInputElement)?.checked ?? true,
    show_mature_content: (document.getElementById('show_mature_content') as HTMLInputElement)?.checked ?? false,
    show_explicit_content: (document.getElementById('show_explicit_content') as HTMLInputElement)?.checked ?? false,
  };

  // Only include age range if the inputs exist
  if (ageMinInput && ageMaxInput) {
    data.age_min = parseInt(ageMinInput.value);
    data.age_max = parseInt(ageMaxInput.value);
  }

  // Only include distance if the input exists (it's on the Filters page, not Preferences)
  // When unchecked = unlimited, when checked = specific distance
  if (distanceInput) {
    data.distance_max_km = !distanceEnabledCheckbox?.checked
      ? DISTANCE_SLIDER.UNLIMITED
      : sliderToKm(parseInt(distanceInput.value), getUnits());
  }

  // Collect filters_json fields
  const filtersJson: Record<string, any> = {};

  // Search filters - interested_in and preferred_structure (flat, no discovery wrapper)
  const interestedIn = getCheckboxGroupValue('interested_in');
  filtersJson.interested_in = interestedIn;

  const preferredStructure = getRadioGroupValue('preferred_structure');
  filtersJson.preferred_structure = preferredStructure || null;

  // Intent section (still nested)
  const relationshipIntents = getCheckboxGroupValue('relationship_intents');
  setNestedValue(filtersJson, 'intent.relationship_intents', relationshipIntents);

  // Search filters (flat)
  const filterHasKids = getRadioGroupValue('filter_has_kids');
  filtersJson.has_kids = filterHasKids || null;

  const filterBodyTypes = getCheckboxGroupValue('filter_body_types');
  filtersJson.body_types = filterBodyTypes;

  const filterEthnicities = getCheckboxGroupValue('filter_ethnicities');
  filtersJson.ethnicities = filterEthnicities;

  const filterPoliticalViews = getCheckboxGroupValue('filter_political_views');
  filtersJson.political_views = filterPoliticalViews;

  const filterEducationLevels = getCheckboxGroupValue('filter_education_levels');
  filtersJson.education_levels = filterEducationLevels;

  const filterEmploymentStatuses = getCheckboxGroupValue('filter_employment_statuses');
  filtersJson.employment_statuses = filterEmploymentStatuses;

  const filterSmoking = getCheckboxGroupValue('filter_smoking');
  filtersJson.smoking = filterSmoking;

  const filterDrinking = getCheckboxGroupValue('filter_drinking');
  filtersJson.drinking = filterDrinking;

  const filterCannabis = getCheckboxGroupValue('filter_cannabis');
  filtersJson.cannabis = filterCannabis;

  const filterDrugs = getCheckboxGroupValue('filter_drugs');
  filtersJson.drugs = filterDrugs;

  const filterZodiacSigns = getCheckboxGroupValue('filter_zodiac_signs');
  filtersJson.zodiac_signs = filterZodiacSigns;

  const filterExerciseLevels = getCheckboxGroupValue('filter_exercise_levels');
  filtersJson.exercise_levels = filterExerciseLevels;

  const filterIncomeRanges = getCheckboxGroupValue('filter_income_ranges');
  filtersJson.income_ranges = filterIncomeRanges;

  const filterOpenToRelocation = getCheckboxGroupValue('filter_open_to_relocation');
  filtersJson.open_to_relocation = filterOpenToRelocation;

  const filterOpenToLongDistance = getCheckboxGroupValue('filter_open_to_long_distance');
  filtersJson.open_to_long_distance = filterOpenToLongDistance;

  const filterHeightMinCm = getNumberValue('filter_height_min_cm');
  if (filterHeightMinCm !== null) filtersJson.height_min_cm = filterHeightMinCm;

  const filterHeightMaxCm = getNumberValue('filter_height_max_cm');
  if (filterHeightMaxCm !== null) filtersJson.height_max_cm = filterHeightMaxCm;

  // App preferences - units (still nested)
  const unitsRadio = document.querySelector('input[name="units_preference"]:checked') as HTMLInputElement | null;
  if (unitsRadio) {
    setNestedValue(filtersJson, 'app.units', unitsRadio.value);
  }

  // Add filters_json to data if not empty
  if (Object.keys(filtersJson).length > 0) {
    data.filters_json = filtersJson;
  }

  try {
    const res = await preferencesApi.update(data);
    const state = store.getState();
    if (state.user) {
      store.setUser({
        ...state.user,
        preferences: res.data,
      });
    }
    return true;
  } catch (err) {
    console.error('Failed to save preferences:', err);
    return false;
  }
}

/**
 * Save visibility settings
 */
export async function saveVisibility(): Promise<boolean> {
  const discoverableInput = document.getElementById('discoverable') as HTMLInputElement;
  if (!discoverableInput) return false;

  try {
    const res = await preferencesApi.updateVisibility({
      discoverable: discoverableInput.checked,
    });
    const state = store.getState();
    if (state.user && state.user.preferences) {
      store.setUser({
        ...state.user,
        preferences: {
          ...state.user.preferences,
          discoverable: res.data.discoverable,
          visi_json: res.data.visi_json,
        },
      });
    }
    return true;
  } catch (err) {
    console.error('Failed to save visibility:', err);
    return false;
  }
}

/**
 * Save field-level visibility setting
 */
export async function saveFieldVisibility(fieldName: string, visible: boolean): Promise<boolean> {
  try {
    const res = await preferencesApi.updateVisibility({ [fieldName]: visible });
    const state = store.getState();
    if (state.user && state.user.preferences) {
      store.setUser({
        ...state.user,
        preferences: {
          ...state.user.preferences,
          discoverable: res.data.discoverable,
          visi_json: res.data.visi_json,
        },
      });
    }
    return true;
  } catch (err) {
    console.error('Failed to save field visibility:', err);
    return false;
  }
}

/**
 * Helper to check if element is a radio group
 */
function isRadioGroup(element: HTMLElement): boolean {
  return element.classList.contains('radio-group') || element.classList.contains('radio-group-with-other');
}

/**
 * Update the profile nav badge instantly from DOM state (no server round-trip).
 * Only runs when the profile form is in the DOM.
 */
export function updateProfileBadgeFromDOM(): void {
  const count = countUnansweredFromDOM();
  if (!document.getElementById('tab-about')) return;
  const badge = document.getElementById('profile-badge');
  if (badge) {
    badge.textContent = count > 0 ? String(count) : '';
    badge.style.display = count > 0 ? 'inline' : 'none';
  }
  updateSectionProgress();
  updateFieldAnsweredStates();
}

/**
 * Setup auto-save event listeners for all form fields
 */
export function setupAutoSave(updateLocationDisplay: () => void, updateAgeDisplay: () => void): void {
  // Profile fields (regular columns)
  const profileFields = ['birth_date', 'gender_id', 'relationship_status_id', 'city', 'state', 'country', 'school', 'company', 'job_title', 'height_cm'];

  // Set of fields that are moderated and should use moderation-aware indicators
  const moderatedFieldSet = new Set(['bio', 'school', 'company', 'job_title']);

  profileFields.forEach(fieldId => {
    const element = document.getElementById(fieldId);
    if (element) {
      if (isRadioGroup(element)) {
        // Radio group - deselectable
        setupDeselectableRadios(element, () => {
          debounce('profile', async () => {
            const success = await saveProfile();
            showSaveIndicator(`indicator-${getIndicatorId(fieldId)}`, success);
            if (success) updateLocationDisplay();
          });
        });
      } else {
        const eventType = element.tagName === 'SELECT' ? 'change' : 'input';
        element.addEventListener(eventType, () => {
          debounce('profile', async () => {
            const success = await saveProfile();
            if (moderatedFieldSet.has(fieldId)) {
              showSaveIndicatorWithModeration(`indicator-${getIndicatorId(fieldId)}`, success, fieldId);
            } else {
              showSaveIndicator(`indicator-${getIndicatorId(fieldId)}`, success);
            }
            if (success) updateLocationDisplay();
          });
        });
      }
    }
  });

  // Profile JSON fields (stored in profile_json column)
  const profileJsonFields = [
    'religion', 'political_view', 'zodiac_sign', 'hometown',
    'weight_lb', 'weight_kg', 'body_type',
    'smoking', 'drinking', 'cannabis', 'drugs', 'exercise',
    'has_kids', 'custody', 'wants_more_kids',
    'education_level', 'employment_status', 'major', 'career_field', 'income_range',
    'interests',
    'open_to_relocation', 'open_to_long_distance',
    'relationship_structure',
  ];

  const moderatedJsonFieldSet = new Set(['hometown', 'interests']);

  profileJsonFields.forEach(fieldId => {
    const element = document.getElementById(fieldId);
    if (element) {
      if (isRadioGroup(element)) {
        // Radio group - deselectable
        setupDeselectableRadios(element, () => {
          debounce('profile', async () => {
            const success = await saveProfile();
            showSaveIndicator(`indicator-${getIndicatorId(fieldId)}`, success);
          });
        });
      } else {
        const eventType = element.tagName === 'SELECT' ? 'change' : 'input';
        element.addEventListener(eventType, () => {
          debounce('profile', async () => {
            const success = await saveProfile();
            if (moderatedJsonFieldSet.has(fieldId)) {
              showSaveIndicatorWithModeration(`indicator-${getIndicatorId(fieldId)}`, success, fieldId);
            } else {
              showSaveIndicator(`indicator-${getIndicatorId(fieldId)}`, success);
            }
          });
        });
      }
    }
  });

  // Profile JSON "other" text fields (for dropdown + other pattern)
  const otherTextFields = [
    'religion_other', 'major_other', 'career_field_other',
    'sexual_orientation_other', 'languages_other',
    'pronouns_custom',  // pronouns uses separate custom field
  ];

  otherTextFields.forEach(fieldId => {
    const element = document.getElementById(fieldId);
    if (element) {
      element.addEventListener('input', () => {
        // Get the parent field name (remove _other suffix)
        const parentFieldId = fieldId.replace('_other', '');
        debounce('profile', async () => {
          const success = await saveProfile();
          if (fieldId === 'pronouns_custom') {
            showSaveIndicatorWithModeration(`indicator-${parentFieldId}`, success, 'pronouns');
          } else {
            showSaveIndicator(`indicator-${parentFieldId}`, success);
          }
        });
      });
    }
  });

  // Profile JSON multi-select checkbox groups (with "other" or "custom" support)
  const multiSelectGroups = ['pronouns', 'sexual_orientation', 'languages'];
  multiSelectGroups.forEach(groupName => {
    const checkboxes = document.querySelectorAll(`input[name="${groupName}"]`);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator(`indicator-${groupName}`, success);
        });
      });
    });
  });

  // Profile JSON checkbox groups (pets, relationship_intent, ethnicity)
  const profileJsonCheckboxGroups = [
    'pets_has', 'pets_wants', 'relationship_intent', 'ethnicity',
    'sexual_role', 'sexual_dynamics',
  ];
  profileJsonCheckboxGroups.forEach(groupName => {
    const checkboxes = document.querySelectorAll(`input[name="${groupName}"]`);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator(`indicator-${groupName}`, success);
        });
      });
    });
  });

  // Profile intent entry handlers (compound gender/intent/age cards)
  setupProfileIntentEntryHandlers();

  // Disclosure toggles
  const disclosureToggles = ['inclusive_of_trans_nb', 'open_to_enm_poly', 'no_other_groups', 'disclosures_pnta'];
  disclosureToggles.forEach(toggleId => {
    const toggle = document.getElementById(toggleId);
    if (toggle) {
      toggle.addEventListener('change', () => {
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator('indicator-disclosures', success);
        });
      });
    }
  });

  // Update age display when birthday changes
  const birthDateInput = document.getElementById('birth_date');
  if (birthDateInput) {
    birthDateInput.addEventListener('input', updateAgeDisplay);
  }

  // Age range
  setupAgeRangeListeners();

  // Distance max slider
  setupDistanceListener();

  // Live typing toggle
  setupLiveTypingToggle();

  // Units preference radio buttons
  setupUnitsListener();

  // Discoverable toggle
  setupDiscoverableToggle();

  // Phone number
  setupPhoneListener();

  // Email change
  setupEmailListener();

  // Preferences JSON fields and checkbox groups
  setupPreferencesJsonListeners();

  // Height filter range
  setupHeightFilterListeners();

  // Field visibility toggles
  setupFieldVisibilityToggles();

  // Handle (vanity URL)
  setupHandleListener();

  // Character counters for headline and bio
  setupCharCounterListeners();

  // Save buttons for headline and bio (explicit save, no auto-save on input)
  setupHeadlineBioSaveButtons();

  // Exclusive checkbox behavior (pnta, none, etc.)
  setupExclusiveCheckboxes();

  // PNTA checkbox handlers for text fields
  setupPntaCheckboxes();

  // PNTA checkbox handlers for radio/checkbox groups
  setupGroupPntaCheckboxes();

  // Instant badge update on any input change (no debounce).
  // Uses event delegation so one listener covers all profile fields.
  const profileForm = document.getElementById('tab-about');
  if (profileForm) {
    profileForm.addEventListener('change', updateProfileBadgeFromDOM);
  }
}

/**
 * Setup listeners for character counter updates
 */
function setupCharCounterListeners(): void {
  const headlineInput = document.getElementById('headline');
  const bioInput = document.getElementById('bio');

  if (headlineInput) {
    headlineInput.addEventListener('input', () => {
      updateCharCounter('headline', 'headline-counter', MAX_HEADLINE_LENGTH);
    });
  }

  if (bioInput) {
    bioInput.addEventListener('input', () => {
      updateCharCounter('bio', 'bio-counter', MAX_BIO_LENGTH);
    });
  }
}

/**
 * Setup Save button click handlers for headline and bio fields
 * These use explicit save instead of auto-save on input
 */
function setupHeadlineBioSaveButtons(): void {
  const saveFirstNameBtn = document.getElementById('save-first-name');
  if (saveFirstNameBtn) {
    saveFirstNameBtn.addEventListener('click', async () => {
      const success = await saveProfile();
      showSaveIndicatorWithModeration('indicator-user_display_str', success, 'user_display_str');
    });
  }

  const saveHeadlineBtn = document.getElementById('save-headline');
  if (saveHeadlineBtn) {
    saveHeadlineBtn.addEventListener('click', async () => {
      const success = await saveProfile();
      showSaveIndicatorWithModeration('indicator-headline', success, 'headline');
    });
  }

  const saveBioBtn = document.getElementById('save-bio');
  if (saveBioBtn) {
    saveBioBtn.addEventListener('click', async () => {
      const success = await saveProfile();
      showSaveIndicatorWithModeration('indicator-bio', success, 'bio');
    });
  }
}

/**
 * Setup listeners for filters_json fields
 */
function setupPreferencesJsonListeners(): void {
  // Interested in checkbox group
  const interestedInCheckboxes = document.querySelectorAll('input[name="interested_in"]');
  interestedInCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-interested_in', success);
    });
  });

  // Preferred structure radio group (deselectable)
  const preferredStructure = document.getElementById('preferred_structure');
  if (preferredStructure && isRadioGroup(preferredStructure)) {
    setupDeselectableRadios(preferredStructure, async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-preferred_structure', success);
    });
  }

  // Relationship intent checkbox group
  const intentCheckboxes = document.querySelectorAll('input[name="relationship_intents"]');
  intentCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-relationship_intents', success);
    });
  });

  // Discovery filter radio groups (deselectable)
  const filterRadioGroups = ['filter_has_kids'];
  filterRadioGroups.forEach(fieldId => {
    const element = document.getElementById(fieldId);
    if (element && isRadioGroup(element)) {
      setupDeselectableRadios(element, async () => {
        const success = await savePreferences();
        showSaveIndicator(`indicator-${fieldId}`, success);
      });
    }
  });

  // Discovery filter checkbox groups
  const filterCheckboxGroups = [
    'filter_body_types', 'filter_ethnicities', 'filter_political_views',
    'filter_education_levels', 'filter_employment_statuses',
    'filter_smoking', 'filter_drinking', 'filter_cannabis', 'filter_drugs',
    'filter_income_ranges', 'filter_open_to_relocation', 'filter_open_to_long_distance',
  ];

  filterCheckboxGroups.forEach(groupName => {
    const checkboxes = document.querySelectorAll(`input[name="${groupName}"]`);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', async () => {
        const success = await savePreferences();
        showSaveIndicator(`indicator-${groupName}`, success);
      });
    });
  });
}

/**
 * Setup height filter range listeners
 */
function setupHeightFilterListeners(): void {
  const heightMinInput = document.getElementById('filter_height_min_cm') as HTMLInputElement;
  const heightMaxInput = document.getElementById('filter_height_max_cm') as HTMLInputElement;
  const heightDisplay = document.getElementById('pref-height-display');
  const heightEnabledCheckbox = document.getElementById('height-enabled') as HTMLInputElement;

  if (!heightMinInput || !heightMaxInput || !heightDisplay) return;

  const updateDisplay = () => {
    const units = getUnits();
    // When unchecked = "Any" (disabled), when checked = specific range (enabled)
    if (!heightEnabledCheckbox?.checked) {
      heightDisplay.textContent = 'Any';
      heightMinInput.disabled = true;
      heightMaxInput.disabled = true;
    } else {
      const minCm = parseInt(heightMinInput.value);
      const maxCm = parseInt(heightMaxInput.value);
      heightDisplay.textContent = `${formatHeight(minCm, units)} - ${formatHeight(maxCm, units)}`;
      heightMinInput.disabled = false;
      heightMaxInput.disabled = false;
    }
  };

  heightMinInput.addEventListener('input', () => {
    const minCm = parseInt(heightMinInput.value);
    if (minCm > parseInt(heightMaxInput.value)) {
      heightMaxInput.value = heightMinInput.value;
    }
    updateDisplay();
    debounce('preferences-height', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-height-filter', success);
    });
  });

  heightMaxInput.addEventListener('input', () => {
    const maxCm = parseInt(heightMaxInput.value);
    if (maxCm < parseInt(heightMinInput.value)) {
      heightMinInput.value = heightMaxInput.value;
    }
    updateDisplay();
    debounce('preferences-height', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-height-filter', success);
    });
  });

  heightEnabledCheckbox?.addEventListener('change', () => {
    updateDisplay();
    debounce('preferences-height', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-height-filter', success);
    });
  });

  // Initialize display
  updateDisplay();
}

/**
 * Read intent entry cards from a DOM container and return IntentEntry[]
 */
function collectIntentEntriesFromDOM(listId: string): Array<{ genders: string[]; intents: string[]; age_min?: number; age_max?: number }> {
  const list = document.getElementById(listId);
  if (!list) return [];

  const cards = list.querySelectorAll('.intent-entry-card');
  const entries: Array<{ genders: string[]; intents: string[]; age_min?: number; age_max?: number }> = [];

  cards.forEach(card => {
    const genders: string[] = [];
    card.querySelectorAll('input[data-entry-field="genders"]:checked').forEach(cb => {
      genders.push((cb as HTMLInputElement).value);
    });

    const intents: string[] = [];
    card.querySelectorAll('input[data-entry-field="intents"]:checked').forEach(cb => {
      intents.push((cb as HTMLInputElement).value);
    });

    const ageMinSlider = card.querySelector('.intent-entry-age-min') as HTMLInputElement;
    const ageMaxSlider = card.querySelector('.intent-entry-age-max') as HTMLInputElement;
    const ageMin = ageMinSlider ? parseInt(ageMinSlider.value) : 18;
    const ageMax = ageMaxSlider ? parseInt(ageMaxSlider.value) : 99;

    entries.push({ genders, intents, age_min: ageMin, age_max: ageMax });
  });

  return entries;
}

/**
 * Setup handlers for profile intent entry CRUD (add, remove, edit genders/intents/age).
 */
function setupProfileIntentEntryHandlers(): void {
  const listEl = document.getElementById('profile-intent-entries-list');
  const addBtn = document.getElementById('add-profile-intent-entry-btn');
  if (!listEl || !addBtn) return;

  // Inline renderer to avoid circular imports
  const GENDER_OPTS = [
    { value: 'men', label: 'Man' },
    { value: 'women', label: 'Woman' },
    { value: 'nonbinary', label: 'Non-binary' },
  ];
  const INTENT_OPTS = [
    { value: 'friends', label: 'Friends' },
    { value: 'activity_partner', label: 'Activity Partner' },
    { value: 'dating', label: 'Dating' },
    { value: 'long_term', label: 'Long-term' },
    { value: 'marriage', label: 'Marriage' },
    { value: 'casual', label: 'Casual' },
    { value: 'exploring', label: 'Exploring' },
    { value: 'hookup', label: 'Hookup' },
  ];

  function renderEntriesHtml(entries: Array<{ genders: string[]; intents: string[]; age_min?: number; age_max?: number }>): string {
    if (!entries.length) {
      return '<div class="empty-intent-entries">You haven\'t added anything yet. Add one or more to describe how you want to connect.</div>';
    }
    return entries.map((entry, i) => `
      <div class="intent-entry-card" data-entry-index="${i}">
        <button type="button" class="btn-remove-intent-entry" data-entry-index="${i}" title="Remove">
          <i class="fas fa-times"></i>
        </button>
        <div class="intent-entry-fields">
          <div class="intent-entry-field">
            <span class="group-label">Their Gender</span>
            <div class="checkbox-group">
              ${GENDER_OPTS.map(opt => `
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    name="profile_intent_genders_${i}"
                    value="${opt.value}"
                    ${entry.genders?.includes(opt.value) ? 'checked' : ''}
                    data-entry-index="${i}"
                    data-entry-field="genders"
                  />
                  ${opt.label}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="intent-entry-field">
            <span class="group-label">Connection Goals</span>
            <div class="checkbox-group">
              ${INTENT_OPTS.map(opt => `
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    name="profile_intent_intents_${i}"
                    value="${opt.value}"
                    ${entry.intents?.includes(opt.value) ? 'checked' : ''}
                    data-entry-index="${i}"
                    data-entry-field="intents"
                  />
                  ${opt.label}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="intent-entry-field">
            <div class="preference-row">
              <span class="group-label">Age Range</span>
              <span class="filter-value intent-entry-age-display" data-entry-index="${i}">${(entry.age_min ?? 18) === 18 && (entry.age_max ?? 99) === 99 ? 'Any' : `${entry.age_min ?? 18}-${entry.age_max ?? 99}`}</span>
            </div>
            <div class="flex gap-2">
              <input type="range" class="input intent-entry-age-min" data-entry-index="${i}" min="18" max="99" value="${entry.age_min ?? 18}" />
              <input type="range" class="input intent-entry-age-max" data-entry-index="${i}" min="18" max="99" value="${entry.age_max ?? 99}" />
            </div>
          </div>
        </div>
      </div>
    `).join('');
  }

  // Track entries in memory for re-render
  let entries = collectIntentEntriesFromDOM('profile-intent-entries-list');

  const reRender = () => {
    listEl.innerHTML = renderEntriesHtml(entries);
    bindEntryListeners();
    debounce('profile', async () => {
      const success = await saveProfile();
      showSaveIndicator('indicator-pref-intent-entries', success);
    });
  };

  // Add button
  addBtn.addEventListener('click', () => {
    entries.push({ genders: [], intents: [], age_min: 18, age_max: 99 });
    reRender();
  });

  function bindEntryListeners(): void {
    // Remove buttons
    listEl!.querySelectorAll('.btn-remove-intent-entry').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt((e.currentTarget as HTMLElement).dataset.entryIndex || '0');
        entries.splice(index, 1);
        reRender();
      });
    });

    // Gender checkboxes
    listEl!.querySelectorAll('input[data-entry-field="genders"]').forEach(cb => {
      cb.addEventListener('change', () => {
        // Re-collect from DOM
        entries = collectIntentEntriesFromDOM('profile-intent-entries-list');
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator('indicator-pref-intent-entries', success);
        });
      });
    });

    // Intent checkboxes
    listEl!.querySelectorAll('input[data-entry-field="intents"]').forEach(cb => {
      cb.addEventListener('change', () => {
        entries = collectIntentEntriesFromDOM('profile-intent-entries-list');
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator('indicator-pref-intent-entries', success);
        });
      });
    });

    // Age sliders
    listEl!.querySelectorAll('.intent-entry-age-min, .intent-entry-age-max').forEach(slider => {
      slider.addEventListener('input', () => {
        const input = slider as HTMLInputElement;
        const idx = parseInt(input.dataset.entryIndex || '0');
        const isMin = input.classList.contains('intent-entry-age-min');

        const card = listEl!.querySelector(`.intent-entry-card[data-entry-index="${idx}"]`);
        if (!card) return;

        const minSlider = card.querySelector('.intent-entry-age-min') as HTMLInputElement;
        const maxSlider = card.querySelector('.intent-entry-age-max') as HTMLInputElement;
        if (!minSlider || !maxSlider) return;

        // Enforce min <= max
        if (isMin && parseInt(minSlider.value) > parseInt(maxSlider.value)) {
          maxSlider.value = minSlider.value;
        } else if (!isMin && parseInt(maxSlider.value) < parseInt(minSlider.value)) {
          minSlider.value = maxSlider.value;
        }

        // Update display
        const display = card.querySelector('.intent-entry-age-display');
        if (display) {
          const min = parseInt(minSlider.value);
          const max = parseInt(maxSlider.value);
          display.textContent = (min === 18 && max === 99) ? 'Any' : `${min}-${max}`;
        }
      });

      slider.addEventListener('change', () => {
        entries = collectIntentEntriesFromDOM('profile-intent-entries-list');
        debounce('profile', async () => {
          const success = await saveProfile();
          showSaveIndicator('indicator-pref-intent-entries', success);
        });
      });
    });
  }

  // Initial binding
  bindEntryListeners();
}

function setupAgeRangeListeners(): void {
  const ageMinInput = document.getElementById('age_min') as HTMLInputElement;
  const ageMaxInput = document.getElementById('age_max') as HTMLInputElement;
  const ageDisplay = document.getElementById('pref-age-display');
  const ageEnabledCheckbox = document.getElementById('age-enabled') as HTMLInputElement;

  if (!ageMinInput || !ageMaxInput || !ageDisplay) return;

  const updateDisplay = () => {
    // When unchecked = "Any" (disabled), when checked = specific range (enabled)
    if (!ageEnabledCheckbox?.checked) {
      ageDisplay.textContent = 'Any';
      ageMinInput.disabled = true;
      ageMaxInput.disabled = true;
      ageMinInput.value = MIN_AGE.toString();
      ageMaxInput.value = MAX_AGE.toString();
    } else {
      const min = ageMinInput.value;
      const max = ageMaxInput.value;
      ageDisplay.textContent = `${min}-${max}`;
      ageMinInput.disabled = false;
      ageMaxInput.disabled = false;
    }
  };

  ageMinInput.addEventListener('input', () => {
    if (parseInt(ageMinInput.value) > parseInt(ageMaxInput.value)) {
      ageMaxInput.value = ageMinInput.value;
    }
    updateDisplay();
    debounce('preferences-age', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-age', success);
    });
  });

  ageMaxInput.addEventListener('input', () => {
    if (parseInt(ageMaxInput.value) < parseInt(ageMinInput.value)) {
      ageMinInput.value = ageMaxInput.value;
    }
    updateDisplay();
    debounce('preferences-age', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-age', success);
    });
  });

  ageEnabledCheckbox?.addEventListener('change', () => {
    updateDisplay();
    debounce('preferences-age', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-age', success);
    });
  });

  // Initialize display
  updateDisplay();
}

function setupDistanceListener(): void {
  const distanceInput = document.getElementById('distance_max_km') as HTMLInputElement;
  const distanceDisplay = document.getElementById('distance-display');
  const distanceEnabledCheckbox = document.getElementById('distance-enabled') as HTMLInputElement;

  if (!distanceInput || !distanceDisplay) return;

  const updateDisplay = () => {
    // When unchecked = "Any" (disabled), when checked = specific distance (enabled)
    if (!distanceEnabledCheckbox?.checked) {
      distanceDisplay.textContent = 'Any';
      distanceInput.disabled = true;
    } else {
      const pos = parseInt(distanceInput.value);
      distanceDisplay.textContent = sliderToDisplayLabel(pos, getUnits());
      distanceInput.disabled = false;
    }
  };

  distanceInput.addEventListener('input', () => {
    updateDisplay();
    debounce('preferences-distance', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-distance', success);
    });
  });

  distanceEnabledCheckbox?.addEventListener('change', () => {
    updateDisplay();
    debounce('preferences-distance', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-distance', success);
    });
  });

  // Initialize display state
  updateDisplay();
}

function setupLiveTypingToggle(): void {
  const liveTypingToggle = document.getElementById('live_typing_enabled');
  if (liveTypingToggle) {
    liveTypingToggle.addEventListener('change', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-live_typing', success);
    });
  }

  const matureToggle = document.getElementById('show_mature_content');
  if (matureToggle) {
    matureToggle.addEventListener('change', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-show_mature_content', success);
    });
  }

  const explicitToggle = document.getElementById('show_explicit_content');
  if (explicitToggle) {
    explicitToggle.addEventListener('change', async () => {
      const success = await savePreferences();
      showSaveIndicator('indicator-show_explicit_content', success);
    });
  }
}

function setupUnitsListener(): void {
  const unitsRadios = document.querySelectorAll('input[name="units_preference"]');
  unitsRadios.forEach(radio => {
    radio.addEventListener('change', async () => {
      const selectedUnits = (radio as HTMLInputElement).value;
      const isMetric = selectedUnits === 'metric';

      // Toggle height inputs visibility
      const heightMetric = document.getElementById('height-metric-inputs');
      const heightImperial = document.getElementById('height-imperial-inputs');
      if (heightMetric) heightMetric.style.display = isMetric ? '' : 'none';
      if (heightImperial) heightImperial.style.display = isMetric ? 'none' : '';

      // Sync height values when switching
      const heightCmInput = document.getElementById('height_cm') as HTMLInputElement;
      const heightFeetInput = document.getElementById('height_feet') as HTMLInputElement;
      const heightInchesInput = document.getElementById('height_inches') as HTMLInputElement;
      if (heightCmInput && heightFeetInput && heightInchesInput) {
        if (isMetric && heightFeetInput.value && heightInchesInput.value) {
          // Convert ft/in to cm
          const feet = parseInt(heightFeetInput.value) || 0;
          const inches = parseInt(heightInchesInput.value) || 0;
          heightCmInput.value = Math.round(feet * 30.48 + inches * 2.54).toString();
        } else if (!isMetric && heightCmInput.value) {
          // Convert cm to ft/in
          const cm = parseInt(heightCmInput.value) || 0;
          const totalInches = cm / 2.54;
          heightFeetInput.value = Math.floor(totalInches / 12).toString();
          heightInchesInput.value = Math.round(totalInches % 12).toString();
        }
      }

      // Toggle weight inputs visibility
      const weightMetric = document.getElementById('weight-metric-inputs');
      const weightImperial = document.getElementById('weight-imperial-inputs');
      if (weightMetric) weightMetric.style.display = isMetric ? '' : 'none';
      if (weightImperial) weightImperial.style.display = isMetric ? 'none' : '';

      // Sync weight values when switching
      const weightLbInput = document.getElementById('weight_lb') as HTMLInputElement;
      const weightKgInput = document.getElementById('weight_kg') as HTMLInputElement;
      if (weightLbInput && weightKgInput) {
        if (isMetric && weightLbInput.value) {
          // Convert lb to kg
          weightKgInput.value = Math.round(parseInt(weightLbInput.value) * 0.453592).toString();
        } else if (!isMetric && weightKgInput.value) {
          // Convert kg to lb
          weightLbInput.value = Math.round(parseInt(weightKgInput.value) / 0.453592).toString();
        }
      }

      // Update height filter display
      const heightDisplay = document.getElementById('pref-height-display');
      const heightMinInput = document.getElementById('filter_height_min_cm') as HTMLInputElement;
      const heightMaxInput = document.getElementById('filter_height_max_cm') as HTMLInputElement;
      const heightEnabledCheckbox = document.getElementById('height-enabled') as HTMLInputElement;
      if (heightDisplay && heightMinInput && heightMaxInput) {
        // When unchecked = "Any", when checked = specific range
        if (!heightEnabledCheckbox?.checked) {
          heightDisplay.textContent = 'Any';
        } else {
          const units = isMetric ? 'metric' : 'imperial';
          heightDisplay.textContent = `${formatHeight(parseInt(heightMinInput.value), units)} - ${formatHeight(parseInt(heightMaxInput.value), units)}`;
        }
      }

      // Update distance display
      const distanceDisplay = document.getElementById('distance-display');
      const distanceInput = document.getElementById('distance_max_km') as HTMLInputElement;
      const distanceEnabledCheckbox = document.getElementById('distance-enabled') as HTMLInputElement;
      if (distanceDisplay && distanceInput) {
        // When unchecked = "Any", when checked = specific distance
        if (!distanceEnabledCheckbox?.checked) {
          distanceDisplay.textContent = 'Any';
        } else {
          const pos = parseInt(distanceInput.value);
          distanceDisplay.textContent = sliderToDisplayLabel(pos, isMetric ? 'metric' : 'imperial');
        }
      }

      const success = await savePreferences();
      showSaveIndicator('indicator-units', success);
    });
  });
}

function setupDiscoverableToggle(): void {
  const discoverableToggle = document.getElementById('discoverable');
  if (discoverableToggle) {
    discoverableToggle.addEventListener('change', async () => {
      const success = await saveVisibility();
      showSaveIndicator('indicator-discoverable', success);
    });
  }
}

function setupPhoneListener(): void {
  const phoneInput = document.getElementById('phone') as HTMLInputElement;
  if (!phoneInput) return;

  phoneInput.addEventListener('input', () => {
    debounce('phone', async () => {
      try {
        await profileApi.updatePhone(phoneInput.value || null);
        // Update state
        const state = store.getState();
        if (state.user) {
          store.setUser({
            ...state.user,
            phone_number: phoneInput.value || null,
          });
        }
        showSaveIndicator('indicator-phone', true);
      } catch (err) {
        console.error('Failed to save phone:', err);
        showSaveIndicator('indicator-phone', false);
      }
    });
  });
}

function setupEmailListener(): void {
  const emailInput = document.getElementById('email') as HTMLInputElement;
  const emailVerifyStatus = document.getElementById('email-verify-status');
  let originalEmail = store.getState().user?.email || '';
  let pendingEmail = '';

  if (!emailInput || !emailVerifyStatus) return;

  emailInput.addEventListener('input', () => {
    debounce('email', async () => {
      const newEmail = emailInput.value.trim();
      if (newEmail === originalEmail) {
        emailVerifyStatus.textContent = '';
        return;
      }

      if (!newEmail) {
        emailVerifyStatus.textContent = '';
        return;
      }

      try {
        const response = await profileApi.changeEmail(newEmail);
        if (response.data.pending) {
          pendingEmail = newEmail;
          emailVerifyStatus.innerHTML = `Verify code emailed. Or <a href="#" id="enter-code-link">enter code here</a>.`;

          // Setup click handler for enter code link
          const enterCodeLink = document.getElementById('enter-code-link');
          enterCodeLink?.addEventListener('click', (e) => {
            e.preventDefault();
            showEmailVerifyModal(pendingEmail, () => {
              // On success, update state and UI
              originalEmail = pendingEmail;
              emailVerifyStatus.textContent = '';
              showSaveIndicator('indicator-email', true);
              showToast(TOAST_EMAIL_UPDATED, 'success');
            });
          });
        }
      } catch (err: any) {
        console.error('Failed to request email change:', err);
        emailVerifyStatus.textContent = err.response?.data?.error || 'Failed to send verification';
        emailVerifyStatus.classList.add('age-display-error');
        showSaveIndicator('indicator-email', false);
      }
    });
  });
}

/** Check if an .input-group has any real answer (radio checked, checkbox checked, or text value). */
function isInputGroupAnswered(group: Element): boolean {
  const radioGroup = group.querySelector('.radio-group, .radio-group-with-other');
  if (radioGroup) {
    return radioGroup.querySelector('input[type="radio"]:checked') !== null;
  }

  const checkboxGroup = group.querySelector('.checkbox-group');
  if (checkboxGroup) {
    return checkboxGroup.querySelector('input[type="checkbox"]:checked') !== null;
  }

  const inputs = group.querySelectorAll('input.input, textarea.input');
  if (inputs.length > 0) {
    return Array.from(inputs).some(
      input => (input as HTMLInputElement).value.trim() !== ''
    );
  }

  return false;
}

/**
 * Setup listeners for field visibility toggles (all [data-visi-field] checkboxes)
 */
function setupFieldVisibilityToggles(): void {
  const checkboxes = document.querySelectorAll<HTMLInputElement>('[data-visi-field]');

  checkboxes.forEach((checkbox) => {
    const field = checkbox.dataset.visiField;
    if (!field) return;

    checkbox.addEventListener('change', async (e) => {
      // Prevent section header collapse when clicking visi-check
      e.stopPropagation();

      // Auto-select PNTA when hiding an unanswered field
      if (checkbox.checked) {
        const inputGroup = checkbox.closest('.input-group');
        if (inputGroup) {
          const pntaCb = inputGroup.querySelector(
            '.pnta-checkbox input[type="checkbox"]:not([data-visi-field])'
          ) as HTMLInputElement | null;
          if (pntaCb && !pntaCb.checked && !isInputGroupAnswered(inputGroup)) {
            pntaCb.checked = true;
            pntaCb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      const success = await saveFieldVisibility(field, !checkbox.checked);
      // Show the nearest save indicator in the same input-group
      const indicator = checkbox.closest('.input-group')?.querySelector('.save-indicator');
      if (indicator?.id) {
        showSaveIndicator(indicator.id, success);
      }
    });

    // Also prevent click from bubbling to section header
    checkbox.closest('.visi-check')?.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  });
}

/**
 * Setup listener for handle field (vanity URL)
 */
function setupHandleListener(): void {
  const handleInput = document.getElementById('handle') as HTMLInputElement;
  if (!handleInput) return;

  handleInput.addEventListener('input', () => {
    debounce('handle', async () => {
      const handle = handleInput.value.trim();

      // Validate locally first
      if (handle.length > 0 && handle.length < 3) {
        showSaveIndicator('indicator-handle', false, 'Min 3 characters');
        return;
      }
      if (handle.length > 20) {
        showSaveIndicator('indicator-handle', false, 'Max 20 characters');
        return;
      }
      if (handle && !/^[a-zA-Z0-9]+$/.test(handle)) {
        showSaveIndicator('indicator-handle', false, 'Letters and numbers only');
        return;
      }

      // Empty handle - don't save (user might be clearing to type a new one)
      if (!handle) {
        return;
      }

      try {
        await profileApi.updateHandle(handle);
        // Update state
        const state = store.getState();
        if (state.user?.profile) {
          store.setUser({
            ...state.user,
            profile: {
              ...state.user.profile,
              handle,
            },
          });
        }
        showSaveIndicator('indicator-handle', true);
      } catch (err: any) {
        console.error('Failed to save handle:', err);
        const errorMessage = err.response?.data?.error || 'Failed to save';
        showSaveIndicator('indicator-handle', false, errorMessage);
      }
    });
  });
}

/**
 * Setup mutual exclusivity for checkbox groups
 * When an exclusive option (pnta, none, no_other_groups) is checked, uncheck ALL others (including other exclusive options)
 * When any other option is checked, uncheck all exclusive options
 */
function setupExclusiveCheckboxes(): void {
  // Find all checkboxes with data-exclusive attribute
  const exclusiveCheckboxes = document.querySelectorAll('input[type="checkbox"][data-exclusive="true"]');

  exclusiveCheckboxes.forEach(exclusiveCheckbox => {
    const exclusiveCb = exclusiveCheckbox as HTMLInputElement;
    const groupName = exclusiveCb.name;

    // When exclusive option is checked, uncheck ALL others (including other exclusive options)
    exclusiveCb.addEventListener('change', () => {
      if (exclusiveCb.checked) {
        const allOtherCheckboxes = document.querySelectorAll(
          `input[name="${groupName}"]`
        );
        allOtherCheckboxes.forEach(cb => {
          if (cb !== exclusiveCb) {
            (cb as HTMLInputElement).checked = false;
          }
        });
      }
    });

    // When any non-exclusive option is checked, uncheck ALL exclusive options
    const nonExclusiveCheckboxes = document.querySelectorAll(
      `input[name="${groupName}"]:not([data-exclusive="true"])`
    );
    nonExclusiveCheckboxes.forEach(cb => {
      cb.addEventListener('change', () => {
        if ((cb as HTMLInputElement).checked) {
          // Uncheck all exclusive options in the group
          const allExclusiveInGroup = document.querySelectorAll(
            `input[name="${groupName}"][data-exclusive="true"]`
          );
          allExclusiveInGroup.forEach(exCb => {
            (exCb as HTMLInputElement).checked = false;
          });
        }
      });
    });
  });
}

/**
 * Setup PNTA checkbox handlers for text fields
 * When PNTA checked, disable field and set value to '[pnta]' (or ['pnta'] for arrays)
 * When PNTA unchecked, enable field and clear value
 */
function setupPntaCheckboxes(): void {
  const pntaCheckboxes = document.querySelectorAll('input[data-pnta-for]');

  pntaCheckboxes.forEach(pntaCheckbox => {
    const pntaCb = pntaCheckbox as HTMLInputElement;
    const targetFieldIds = pntaCb.dataset.pntaFor?.split(',') || [];
    const isArray = pntaCb.dataset.pntaArray === 'true';

    pntaCb.addEventListener('change', async () => {
      targetFieldIds.forEach(fieldId => {
        const field = document.getElementById(fieldId.trim()) as HTMLInputElement | HTMLTextAreaElement | null;
        if (!field) return;

        if (pntaCb.checked) {
          // Disable field and clear display value
          field.disabled = true;
          field.value = '';
        } else {
          // Enable field and clear value (remove any generated/pnta text)
          field.disabled = false;
          field.value = '';
        }
      });

      // Trigger save
      debounce('profile', async () => {
        const success = await saveProfile();
        const indicatorId = targetFieldIds.length === 1
          ? `indicator-${targetFieldIds[0].trim()}`
          : `indicator-${targetFieldIds[0].trim()}`;
        showSaveIndicator(indicatorId, success);

        // After save, populate first_name input with server-generated private name
        if (success && pntaCb.checked && targetFieldIds.includes('user_display_str')) {
          const updatedName = store.getState().user?.profile?.first_name;
          const field = document.getElementById('user_display_str') as HTMLInputElement | null;
          if (field && updatedName) {
            field.value = updatedName;
          }
        }
      });
    });
  });
}

/**
 * Setup PNTA checkbox handlers for radio/checkbox groups
 * When PNTA checked, uncheck all group options
 * When any group option selected, uncheck PNTA
 */
function setupGroupPntaCheckboxes(): void {
  const pntaCheckboxes = document.querySelectorAll('input[data-pnta-for-group]');

  pntaCheckboxes.forEach(pntaCheckbox => {
    const pntaCb = pntaCheckbox as HTMLInputElement;
    const groupId = pntaCb.dataset.pntaForGroup;
    if (!groupId) return;

    // When PNTA checked, uncheck all group options
    pntaCb.addEventListener('change', () => {
      if (pntaCb.checked) {
        const inputs = document.querySelectorAll(`input[name="${groupId}"]`);
        inputs.forEach(input => {
          (input as HTMLInputElement).checked = false;
        });

        // Also hide any "other" text containers
        const otherContainer = document.getElementById(`${groupId}_other_container`);
        if (otherContainer) {
          otherContainer.style.display = 'none';
        }
        const customContainer = document.getElementById(`${groupId}_custom_container`);
        if (customContainer) {
          customContainer.style.display = 'none';
        }
      }
      // Trigger save via debounce
      debounce('profile', async () => {
        const success = await saveProfile();
        showSaveIndicator(`indicator-${groupId}`, success);
      });
    });

    // When any group option selected, uncheck PNTA
    const groupInputs = document.querySelectorAll(`input[name="${groupId}"]`);
    groupInputs.forEach(input => {
      input.addEventListener('change', () => {
        if ((input as HTMLInputElement).checked) {
          pntaCb.checked = false;
        }
      });
    });
  });
}
